/**
 * Bridge to the Airview Studio iOS app (apps/ios/ArcherField). The app loads this web UI in a WKWebView
 * and injects `window.sightlineNative` at document start; in a normal browser it is absent and
 * every helper here degrades to "not native".
 *
 * Native → web: the app calls `window.__sightlineNativeEmit(type, detail)`, re-dispatched as a
 * `sightline:<type>` CustomEvent on window. Types:
 *   'heading'    {trueDeg: number|null, magneticDeg: number, accuracyDeg: number|null, t: number}
 *   'permission' {location: NativePermission}
 * The app also polyfills `navigator.geolocation` with CoreLocation, so lib/geolocation.ts works
 * unchanged and never shows the web "would like to use your location" prompt.
 *
 * Build 7+ (spoken updates, phone in the pocket): `speaksCallouts` (the app speaks server callouts
 * from its own socket — the web must not speak them too), `setMission` (lib/geolocation.ts
 * NativeMissionSync tells the app which live mission to follow and whether location is shared, so
 * it keeps sharing and speaking with the phone locked) and `speak` (one line now, "test voice").
 * All three are optional: older builds lack them — call with optional chaining.
 */
export type NativePermission = 'granted' | 'denied' | 'prompt';

export interface NativeHeading {
  /** Degrees true (CLHeading.trueHeading); null when iOS has no location to correct with. */
  trueDeg: number | null;
  magneticDeg: number;
  /** ± degrees; null/negative = invalid (needs calibration). */
  accuracyDeg: number | null;
  /** Build 9+: 0 lying flat … 1 held upright (CoreMotion gravity); absent on older builds. */
  upright?: number | null;
  t: number;
}

export interface NativeCameraRequest { requestId: string; missionId: string; aircraftId: string; label: string }
export interface NativeCameraEvent { requestId: string; state: 'opening' | 'open' | 'closed' | 'error'; message?: string }

export interface NativeRadioRequest { requestId: string; missionId: string }
export interface SightlineNative {
  cameraTelemetryAvailable?: boolean;
  cameraBackgroundCollectionAvailable?: boolean;
  requestCameraTelemetryStatus?(): void;
  connectCameraTelemetry?(request: {missionId:string;cameraId:string;host:string;backgroundMinutes?:number}): void;
  disconnectCameraTelemetry?(): void;
  crewRadioAvailable?: boolean;
  cameraPresentationAcknowledgement?: boolean;
  openCrewRadio?(request: NativeRadioRequest): void;
  closeCrewRadio?(requestId: string): void;
  spatialCameraAvailable?: boolean;
  readonly cameraPerspectiveActive?: boolean;
  openCameraPerspective?(request: NativeCameraRequest): void;
  closeCameraPerspective?(requestId: string): void;
  /** Native binary includes the camera usage description needed by WebKit capture. */
  cameraPreviewAvailable?: boolean;
  /** Pause announcements while foreground crew voice owns the audio session. */
  setCrewVoiceActive?(active:boolean):void;
  platform: 'ios';
  /** App version + build, e.g. "1.0 (6)". */
  version: string;
  /** Current iOS location authorization for the app. */
  locationPermission(): NativePermission;
  /** Ask iOS for When-In-Use location (no-op once decided). */
  requestLocationPermission(): void;
  /** Start/stop CLHeading updates ('heading' events). */
  startHeading(): void;
  stopHeading(): void;
  /** Keep the screen on (UIApplication.isIdleTimerDisabled). */
  keepAwake(on: boolean): void;
  /** Open this app's page in iOS Settings (to turn location on). */
  openSettings(): void;
  /** Build 7+: true when the app speaks callouts itself (the web must then not speak them). */
  speaksCallouts?: boolean;
  /**
   * Build 7+: the open mission, whether it is live and whether my location is being shared. The
   * app follows that mission's callouts and, while sharing, keeps posting my location with the
   * phone locked. `missionId: null` stops everything.
   */
  setMission?(m: NativeMissionState): void;
  /** Build 7+: speak one line now (e.g. "test voice"). */
  speak?(text: string): void;
  /** Build 8+: the iOS share sheet (WKWebView has no reliable navigator.share). */
  share?(s: { title?: string; text?: string; url: string }): void;
  /** Build 8+: a vibration — 'important' for takeoff / landing / coming toward you. */
  haptic?(kind: 'important' | 'tap'): void;
  /**
   * Build 12+: speak callouts with this voice (an AVSpeechSynthesisVoice identifier — WebKit's
   * `voiceURI`); null = the best installed voice automatically. Remembered by the app.
   */
  setVoice?(id: string | null): void;
  /** Stop native activity and remove cached account authentication after deletion/sign out. */
  clearAccountData?(): void;
}

export interface NativeMissionState {
  missionId: string | null;
  live: boolean;
  sharing: boolean;
}

declare global {
  interface Window {
    sightlineNative?: SightlineNative;
    __sightlineNativeEmit?: (type: string, detail: unknown) => void;
  }
}

export function getNative(): SightlineNative | null {
  return typeof window !== 'undefined' && window.sightlineNative ? window.sightlineNative : null;
}

export const isNativeApp = (): boolean => getNative() !== null;

/** Subscribe to a native event; returns an unsubscribe. */
export function onNative<T = unknown>(type: 'heading' | 'permission' | 'cameraPerspective' | 'crewRadio', cb: (detail: T) => void): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const h = (e: Event) => cb((e as CustomEvent<T>).detail);
  window.addEventListener(`sightline:${type}`, h);
  return () => window.removeEventListener(`sightline:${type}`, h);
}
